import { useEffect, useState, useRef } from "react";
import { TOPIC_CATEGORIES } from "../../constants/community";
import { uploadImage } from "../../services/communityService";
import EmojiPicker from "./EmojiPicker";

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "vừa xong";
  if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)} ngày trước`;
  return date.toLocaleDateString("vi-VN");
}

// Render text with inline images (image URLs on separate lines)
function RichText({ text }) {
  if (!text) return null;
  const parts = text.split(/(\bhttps?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp)\S*)/gi);
  return parts.map((part, i) =>
    /\bhttps?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp)/i.test(part) ? (
      <img key={i} src={part} alt="" className="reply-image" loading="lazy" onClick={() => window.open(part, '_blank')} />
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

export default function TopicDetail({
  topic, loading, session, submitting,
  onBack, onCreateReply, onDeleteTopic, onDeleteReply, onLoadDetail,
}) {
  const [replyText, setReplyText] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const myUserId = session?.user?.id;

  useEffect(() => {
    if (topic && !topic.replies) {
      onLoadDetail(topic.id);
    }
  }, [topic]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!topic && !loading) return null;

  const cat = topic ? TOPIC_CATEGORIES.find(c => c.id === topic.category) : null;

  async function handleReply(e) {
    e.preventDefault();
    if (!replyText.trim() && !imagePreview) return;

    let finalText = replyText.trim();
    if (imagePreview) {
      finalText = finalText ? `${finalText}\n${imagePreview}` : imagePreview;
    }

    const result = await onCreateReply(topic.id, finalText);
    if (result) {
      setReplyText("");
      setImagePreview(null);
    }
  }

  async function handlePaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        await handleImageUpload(file);
        return;
      }
    }
  }

  async function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (file) await handleImageUpload(file);
    e.target.value = "";
  }

  async function handleImageUpload(file) {
    if (file.size > 5 * 1024 * 1024) {
      alert("Ảnh quá lớn (tối đa 5MB)");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setImagePreview(url);
    } catch {
      alert("Không thể tải ảnh lên");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="topic-detail">
      <button className="topic-back-btn" onClick={onBack}>
        ← Quay lại
      </button>

      {loading ? (
        <>
          <div className="skeleton-detail-card">
            <div className="skeleton-row" style={{ width: "40%" }} />
            <div className="skeleton-row" style={{ width: "70%", height: "20px" }} />
            <div className="skeleton-row" style={{ width: "100%" }} />
            <div className="skeleton-row" style={{ width: "85%" }} />
          </div>
          <div className="skeleton-replies">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton-reply">
                <div className="skeleton-circle" />
                <div style={{ flex: 1 }}>
                  <div className="skeleton-row" style={{ width: "30%" }} />
                  <div className="skeleton-row" style={{ width: "80%" }} />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : topic ? (
        <>
          <div className="topic-detail-card">
            <div className="topic-card-header">
              <div className="topic-author">
                {topic.profiles?.avatar_url ? (
                  <img src={topic.profiles.avatar_url} alt="" className="topic-avatar" />
                ) : (
                  <div className="topic-avatar-placeholder">
                    {(topic.profiles?.display_name || "?")[0]}
                  </div>
                )}
                <span className="topic-author-name">
                  {topic.profiles?.display_name || "Ẩn danh"}
                </span>
                <span className="topic-time">{timeAgo(topic.created_at)}</span>
              </div>
              {cat && (
                <span className="topic-category-badge">
                  {cat.emoji} {cat.label}
                </span>
              )}
            </div>
            <h3 className="topic-detail-title">{topic.title}</h3>
            {topic.body && <div className="topic-detail-body"><RichText text={topic.body} /></div>}
            {myUserId === topic.user_id && (
              <button
                className="topic-delete-btn"
                onClick={() => {
                  if (window.confirm("Bạn có chắc muốn xóa chủ đề này?")) {
                    onDeleteTopic(topic.id);
                  }
                }}
              >
                🗑️ Xóa
              </button>
            )}
          </div>

          <div className="topic-replies-section">
            <div className="topic-replies-header">
              💬 {topic.reply_count || 0} bình luận
            </div>

            {(topic.replies || []).length === 0 ? (
              <div className="community-empty" style={{ padding: "20px" }}>
                Chưa có bình luận. Hãy là người đầu tiên!
              </div>
            ) : (
              <div className="topic-replies-list">
                {topic.replies.map(reply => (
                  <div key={reply.id} className="topic-reply">
                    <div className="topic-reply-header">
                      <div className="topic-author">
                        {reply.profiles?.avatar_url ? (
                          <img src={reply.profiles.avatar_url} alt="" className="topic-avatar-small" />
                        ) : (
                          <div className="topic-avatar-placeholder small">
                            {(reply.profiles?.display_name || "?")[0]}
                          </div>
                        )}
                        <span className="topic-author-name">
                          {reply.profiles?.display_name || "Ẩn danh"}
                        </span>
                        <span className="topic-time">{timeAgo(reply.created_at)}</span>
                      </div>
                      {myUserId === reply.user_id && (
                        <button
                          className="topic-reply-delete"
                          onClick={() => onDeleteReply(reply.id, topic.id)}
                          title="Xóa bình luận"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="topic-reply-body"><RichText text={reply.body} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}

      {/* Reply input - always visible */}
      <form className="topic-reply-form" onSubmit={handleReply}>
        <div className="reply-input-wrapper">
          <textarea
            className="topic-reply-input"
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            onPaste={handlePaste}
            placeholder="Viết bình luận... (có thể paste ảnh)"
            maxLength={2000}
            rows={2}
          />
          {imagePreview && (
            <div className="image-preview">
              <img src={imagePreview} alt="preview" />
              <button type="button" className="image-preview-remove" onClick={() => setImagePreview(null)}>✕</button>
            </div>
          )}
          {uploading && (
            <div className="image-uploading">
              <div className="upload-spinner" /> Đang tải ảnh...
            </div>
          )}
        </div>
        <div className="reply-actions">
          <button type="button" className="reply-image-btn" onClick={() => fileInputRef.current?.click()} title="Đính kèm ảnh">
            🖼️
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileSelect} />
          <EmojiPicker onSelect={emoji => setReplyText(prev => prev + emoji)} />
          <button
            type="submit"
            className="topic-reply-submit"
            disabled={(!replyText.trim() && !imagePreview) || submitting || uploading}
          >
            {submitting ? "..." : "Gửi"}
          </button>
        </div>
      </form>
    </div>
  );
}
